/**
 * Consolidated FFE Maintenance Templates
 * Simplified approach: One template per equipment type
 * Source: UK-MARITIME-LIBRARY FFE_v2 domain (consolidated)
 */

/**
 * Simplified FFE maintenance templates - one per equipment category
 */
export const FFE_TEMPLATES = {
  // 1. EXTINGUISHER MAINTENANCE - MONTHLY VISUAL CHECK
  'ffe.maint.extinguisher.visual': {
    title: 'Fire Extinguisher - Monthly Visual Check',
    description: 'Monthly visual inspection of all extinguishers',
    interval: 'P1M',           // Monthly visual check
    intervalMonths: 1,
    regulation: 'BS 5306-3:2017',
    category: 'FFE',
    priority: 'recommended',   // Recommended grouped check
    frequencySource: 'best_practice',
    userOverridable: true,
    notes: 'Monthly visual check: pressure gauge, condition, accessibility. All extinguishers checked together.'
  },

  // 1A. EXTINGUISHER MAINTENANCE - BASIC SERVICE (ANNUAL)
  'ffe.maint.extinguisher.basic': {
    title: 'Fire Extinguisher - Basic Service',
    description: 'Annual basic service for all extinguisher types',
    interval: 'P1Y',           // Annual basic service (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 16.3.1.8/MGN 276 Table 1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'regulatory',
    userOverridable: false,
    notes: 'CSV: "Every year" basic service required. Covers all types: water, powder, CO2, sealed powder.'
  },

  // 1B. EXTINGUISHER MAINTENANCE - EXTENDED SERVICE (5 YEARS)
  'ffe.maint.extinguisher.extended': {
    title: 'Fire Extinguisher - Extended Service',
    description: 'Extended service with test discharge (5 year cycle)',
    interval: 'P5Y',           // 5-yearly extended service (per CSV)
    intervalMonths: 60,
    regulation: 'WBC3 16.3.1.8/MGN 276 Table 1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'regulatory',
    userOverridable: false,
    notes: 'CSV: "Every 5 years" extended service with test discharge. All extinguisher types except sealed powder.'
  },

  // 1C. EXTINGUISHER MAINTENANCE - OVERHAUL (10 YEARS)  
  'ffe.maint.extinguisher.overhaul': {
    title: 'Fire Extinguisher - Overhaul',
    description: 'Complete overhaul with hydraulic test (10 year cycle)',
    interval: 'P10Y',          // 10-yearly overhaul (per CSV)
    intervalMonths: 120,
    regulation: 'WBC3 16.3.1.8/MGN 276 Table 1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'regulatory',
    userOverridable: false,
    notes: 'CSV: "Every 10 years" complete overhaul with hydraulic test. All extinguisher types.'
  },

  // 2. FIXED FIRE SYSTEMS - SPACE-SPECIFIC (WBC3 Compliant)
  
  // 2A. ENGINE SPACE FIXED SYSTEM
  'ffe.maint.fixed.engine': {
    title: 'Engine Space Fixed Fire System - Annual Service',
    description: 'Annual service of engine/machinery space fixed suppression system',
    interval: 'P1Y',           // Annual minimum (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 16.4.1.2',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Engine/machinery space system. WBC3: "serviced at minimum on an annual interval" or per manufacturer if more frequent.',
    spaceType: 'engine',
    metadata: {
      location: 'Engine/Machinery Space',
      systemType: 'fixed_suppression'
    }
  },
  
  // 2B. GALLEY FIXED SYSTEM (typically wet chemical for cooking)
  'ffe.maint.fixed.galley': {
    title: 'Galley Fixed Fire System - Annual Service',
    description: 'Annual service of galley/cooking area fixed suppression system',
    interval: 'P1Y',           // Annual minimum (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 16.4.1.2/16.3.1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Galley/cooking area system. Often wet chemical type per WBC3 16.3.1 open-flame requirements.',
    spaceType: 'galley',
    metadata: {
      location: 'Galley/Cooking Area',
      systemType: 'fixed_suppression'
    }
  },
  
  // 2C. ACCOMMODATION FIXED SYSTEM
  'ffe.maint.fixed.accommodation': {
    title: 'Accommodation Fixed Fire System - Annual Service',
    description: 'Annual service of accommodation space fixed suppression system',
    interval: 'P1Y',           // Annual minimum (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 16.4.1.2',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Accommodation/living space system. WBC3: "serviced at minimum on an annual interval" or per manufacturer if more frequent.',
    spaceType: 'accommodation',
    metadata: {
      location: 'Accommodation Spaces',
      systemType: 'fixed_suppression'
    }
  },

  // 2D. LEGACY - DEPRECATED (kept for backward compatibility, not emitted in new configs)
  'ffe.maint.fixed.annual': {
    title: 'Fixed Fire System - Annual Service [DEPRECATED]',
    description: 'Annual service by manufacturer specs - Use space-specific templates instead',
    interval: 'P1Y',           // Annual minimum (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 16.4.1.2',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: '[DEPRECATED] Generic fixed system template. Use space-specific templates (.engine, .galley, .accommodation) for WBC3 compliance.',
    deprecated: true
  },

  // 3. CONSOLIDATED FIRE DETECTION (Per manufacturer - defaulted to monthly)
  'ffe.maint.detector.fire.consolidated': {
    title: 'Fire Detection System Maintenance',
    description: 'Testing and servicing per manufacturer specifications',
    interval: 'P1M',           // Monthly default (per CSV)
    intervalMonths: 1,
    regulation: 'WBC3 15.6.2/EN 54',
    category: 'FFE',
    priority: 'recommended',   // Changed to recommended for grouped checks
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Covers all fire detector types: smoke, heat, flame. Default monthly testing, annual servicing. Frequency can be adjusted per manufacturer specifications.'
  },

  // 4. CONSOLIDATED CO DETECTION (Per manufacturer - defaulted to monthly)
  'ffe.maint.detector.co.consolidated': {
    title: 'CO Detection System Maintenance',
    description: 'Testing and calibration per manufacturer specifications',
    interval: 'P1M',           // Monthly default (per CSV)
    intervalMonths: 1,
    regulation: 'WBC3 15.6.3/MIN 698',
    category: 'FFE',
    priority: 'recommended',   // Changed to recommended for grouped checks
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Monthly testing default, annual calibration. Frequency can be adjusted per manufacturer specifications.',
    
    // Equipment filter for precise targeting - only CO detectors
    equipmentFilter: {
      category: 'FFE',
      types: ['detector'],
      detectorTypes: ['co', 'carbon_monoxide'],
      locations: [] // Any location (accommodation, galley, sleeping, exhaust, etc.)
    },
    
    // Temporarily hide from UI while testing precision
    hidden: true
  },

  // 5. CONSOLIDATED GAS DETECTION (Per manufacturer - defaulted to monthly)
  'ffe.maint.detector.gas.consolidated': {
    title: 'Gas Detection System Maintenance',
    description: 'Testing and calibration of hydrocarbon gas detectors (20% LEL)',
    interval: 'P1M',           // Monthly default (per CSV)
    intervalMonths: 1,
    regulation: 'WBC3 15.6.4.1',
    category: 'FFE',
    priority: 'recommended',   // Changed to recommended for grouped checks
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'Monthly testing at 20% LEL threshold. Frequency can be adjusted per manufacturer specifications.'
  },

  // 6. LITHIUM BATTERY DETECTION (Per manufacturer - defaulted to monthly)
  'ffe.maint.detector.lithium.consolidated': {
    title: 'Li-ion Battery Detection Maintenance',
    description: 'Testing of Li-ion battery detection system',
    interval: 'P1M',           // Monthly default (per CSV)
    intervalMonths: 1,
    regulation: 'WBC3 Annex 1 3.2.1',
    category: 'FFE',
    priority: 'recommended',   // Changed to recommended for grouped checks
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'All three detector types: smoke, heat, and flame. Monthly testing default - adjust per manufacturer specifications.'
  },

  // 7. FIRE FIGHTING EQUIPMENT (As specified - defaulted to annual)
  'ffe.maint.pump.general': {
    title: 'Fire Pump Maintenance',
    description: 'Maintain fire pump per manufacturer specifications',
    interval: 'P1Y',           // Annual default (per CSV: "As specified")
    intervalMonths: 12,
    regulation: 'WBC3 16.4.2.1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'CSV: "As specified" by manufacturer. Default annual interval - adjust per manufacturer schedule.'
  },

  'ffe.maint.hose.general': {
    title: 'Fire Hose Maintenance',
    description: 'Maintain fire hoses per manufacturer specifications',
    interval: 'P1Y',           // Annual default (per CSV: "As specified")
    intervalMonths: 12,
    regulation: 'WBC3 16.4.2.4',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer', 
    userOverridable: true,
    notes: 'CSV: "As specified" by manufacturer. Default annual interval - adjust per manufacturer schedule.'
  },

  'ffe.maint.bucket.inspect': {
    title: 'Fire Bucket Inspection',
    description: 'Inspect buckets and lanyards',
    interval: 'P1Y',           // Annual default (per CSV: "As specified")
    intervalMonths: 12,
    regulation: 'WBC3 16.6.1',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'regulatory',
    userOverridable: true,
    notes: 'CSV: "As specified" - check condition periodically. Default annual interval can be adjusted.'
  },

  'ffe.maint.blanket.general': {
    title: 'Fire Blanket Maintenance',
    description: 'Maintain fire blankets per manufacturer specifications',
    interval: 'P1Y',           // Annual default (per CSV: "As specified")
    intervalMonths: 12,
    regulation: 'WBC3 16.5.1/BS EN 1869:2019',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'manufacturer',
    userOverridable: true,
    notes: 'CSV: "As specified" by manufacturer. BS EN 1869:2019 compliance. Default annual interval - adjust per manufacturer.'
  },

  // 8. DOCUMENTATION
  'ffe.maint.plan.review': {
    title: 'Fire Control Plan Review',
    description: 'Review and update fire control plan',
    interval: 'P1Y',           // Annual or after changes (per CSV)
    intervalMonths: 12,
    regulation: 'WBC3 15.8.3',
    category: 'FFE',
    priority: 'required',
    frequencySource: 'regulatory',
    userOverridable: false,
    notes: 'CSV: "Annual or after changes" to fire equipment. WBC3: "kept up to date". Fixed regulatory requirement.'
  }
};

/**
 * Get template by ID
 */
export function getFFETemplate(templateId) {
  return FFE_TEMPLATES[templateId] || null;
}

/**
 * Get all template IDs
 */
export function getAllFFETemplateIds() {
  return Object.keys(FFE_TEMPLATES);
}

/**
 * Get templates by category/type
 */
export function getFFETemplatesByType(type) {
  return Object.entries(FFE_TEMPLATES)
    .filter(([id, template]) => template.notes?.includes(type))
    .reduce((acc, [id, template]) => ({ ...acc, [id]: template }), {});
}

/**
 * Validate that all template IDs are valid
 */
export function validateTemplateIds(templateIds) {
  return templateIds.every(id => FFE_TEMPLATES.hasOwnProperty(id));
}